import * as React from 'react';
import { useCallback } from 'react';
import { Typography } from 'fronton-react';
import { analyticsUtils } from 'utils';
import { Section } from 'features/page-navigation/types';
import { useTranslation } from 'react-i18next';
import {
  li,
  liActive,
  linkWrapper,
  linkWrapperAccent,
  navigationWrapper,
  typography,
  ul,
  underline,
} from './navigation.styles';
import { cx } from 'linaria';

type LinkProps = Section & {
  isActive: boolean;
  onActivated?: () => void;
} & React.HTMLAttributes<HTMLAnchorElement>;

const Link: React.FC<LinkProps> = ({
  href,
  title,
  badgeNumber,
  isActive,
  onActivated,
  className = '',
  ...htmlProps
}) => {
  return (
    <a
      href={href}
      data-qa="navigation-link"
      className={cx(linkWrapper, isActive && linkWrapperAccent, className)}
      {...htmlProps}
    >
      <Typography className={typography} variant="s" size="subtitle" onClick={onActivated}>
        {title}
        {badgeNumber != null ? <>&nbsp;({badgeNumber})</> : null}
      </Typography>
      <div className={underline} />
    </a>
  );
};

type NavigationProps = {
  sections: Section[];
  activeSectionHref: string;
  isSet?: boolean;
  onSectionActivated?: (section: Section) => void;
} & React.HTMLAttributes<HTMLElement>;

export const Navigation: React.FC<NavigationProps> = ({
  sections,
  activeSectionHref,
  onSectionActivated,
  isSet,
  className = '',
  ...htmlProps
}) => {
  const { t } = useTranslation();
  const sendAnalytics = useCallback(
    (item: Section) => () => {
      analyticsUtils.sendEvent('GA:clickButton', {
        elementBlock: isSet ? 'komplektyDetail' : 'productDetail',
        elementName: 'navigationMenu',
        elementText: item.title,
        elementType: 'button',
      });
    },
    [],
  );
  return (
    <nav
      aria-label={t('sticky-panel.aria-label.nav')}
      className={cx(navigationWrapper, className)}
      {...htmlProps}
    >
      <ul role="menubar" className={ul}>
        {sections.map((item) => {
          const isActive = item.href === activeSectionHref;

          return (
            <li role="none" className={cx(li, isActive && liActive)} key={item.href}>
              <Link
                role="menuitem"
                {...item}
                onClick={sendAnalytics(item)}
                isActive={isActive}
                onActivated={onSectionActivated ? () => onSectionActivated(item) : undefined}
              />
            </li>
          );
        })}
      </ul>
    </nav>
  );
};
